import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { App, AppState } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { toast } from 'sonner';
import { supabase } from '../integrations/supabase/client';

export const DeepLinkHandler = () => {
  const navigate = useNavigate();
  const isProcessing = useRef(false);
  const hasProcessedAuth = useRef(false);
  const lastProcessedTime = useRef<number>(0);
  const isOAuthInProgress = useRef(false);

  // Función para verificar sesión y navegar
  const checkSessionAndNavigate = useCallback(async (source: string) => {
    if (hasProcessedAuth.current || isProcessing.current) {
      console.log(`⏭️ [${source}] Skipping - already processed or processing`);
      return false;
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        console.log(`✅ [${source}] Session found, navigating to feed`);
        hasProcessedAuth.current = true;
        isProcessing.current = false;
        isOAuthInProgress.current = false;
        
        // Navegar primero para traer la app al foreground (importante en Android)
        navigate('/feed', { replace: true });
        
        // Intentar cerrar el browser (funciona en iOS, no-op en Android)
        try {
          await Browser.close();
          console.log('🔒 Browser closed');
        } catch (e) {
          console.log('ℹ️ Browser close skipped (Android or not open)');
        }
        
        return true;
      }
    } catch (error) {
      console.error(`❌ [${source}] Error checking session:`, error);
    }
    return false;
  }, [navigate]);

  useEffect(() => {
    const processOAuthUrl = async (url: string) => {
      console.log('🔗 Deep link received:', url);
      
      // Prevenir procesamiento concurrente
      if (isProcessing.current) {
        console.log('⏭️  Already processing, skipping');
        return;
      }

      // Si ya procesamos un OAuth exitosamente, ignorar más callbacks de OAuth
      if (hasProcessedAuth.current && (url.includes('callback/auth') || url.includes('access_token'))) {
        console.log('⏭️  OAuth already processed successfully, ignoring duplicate');
        return;
      }
      
      try {
        const urlObj = new URL(url);
        const host = urlObj.hostname || '';
        const path = urlObj.pathname || '';
        
        // Procesar OAuth callbacks
        if (host === 'callback' || path.includes('/callback/')) {
          console.log('🔐 OAuth callback detected');
          isProcessing.current = true;
          
          const urlParams = new URLSearchParams(urlObj.hash?.substring(1) || urlObj.search);
          const accessToken = urlParams.get('access_token');
          const refreshToken = urlParams.get('refresh_token');
          const code = urlParams.get('code');
          
          // Procesar tokens directos
          if (accessToken && refreshToken) {
            console.log('🔑 Processing OAuth tokens');
            try {
              await supabase.auth.setSession({
                access_token: accessToken,
                refresh_token: refreshToken,
              });
              console.log('✅ OAuth session established');
              
              // Marcar como procesado exitosamente
              hasProcessedAuth.current = true;
              isOAuthInProgress.current = false;
              
              // Esperar un momento para que el auth state se estabilice
              await new Promise(resolve => setTimeout(resolve, 300));
              
              // Navegar primero para traer la app al foreground (Android)
              navigate('/feed', { replace: true });
              
              // Cerrar el In-App Browser (funciona en iOS)
              try {
                await Browser.close();
                console.log('🔒 In-App Browser closed');
              } catch (e) {
                console.log('ℹ️ Browser close skipped');
              }
            } catch (error) {
              console.error('❌ Error setting session:', error);
              toast.error('Error en la autenticación');
            } finally {
              isProcessing.current = false;
            }
            return;
          }
          
          // Procesar código de autorización
          if (code) {
            console.log('🔑 Processing OAuth code');
            try {
              const { error } = await supabase.auth.exchangeCodeForSession(code);
              
              if (error) {
                console.error('❌ Error exchanging code:', error);
                toast.error('Error en la autenticación');
              } else {
                console.log('✅ OAuth session established from code');
                
                // Marcar como procesado exitosamente
                hasProcessedAuth.current = true;
                isOAuthInProgress.current = false;
                
                // Esperar un momento para que el auth state se estabilice
                await new Promise(resolve => setTimeout(resolve, 300));
                
                // Navegar primero para traer la app al foreground (Android)
                navigate('/feed', { replace: true });
                
                // Cerrar el In-App Browser (funciona en iOS)
                try {
                  await Browser.close();
                  console.log('🔒 In-App Browser closed');
                } catch (e) {
                  console.log('ℹ️ Browser close skipped');
                }
              }
            } catch (error) {
              console.error('❌ Error during code exchange:', error);
              toast.error('Error en la autenticación');
            } finally {
              isProcessing.current = false;
            }
            return;
          }
          
          isProcessing.current = false;
          return;
        }
        
        // Procesar deep links de posts compartidos
        if (host === 'post' || host === 'posts' || path.includes('/post/') || path.includes('/posts/')) {
          const slug = path.replace(/^\/+/, '');
          const postId = slug.split('/').pop();
          if (postId) {
            console.log('📱 Shared post deep link:', postId);
            navigate(`/post/${postId}`, { replace: true });
            toast.info('Abriendo publicación compartida 📱');
          }
          return;
        }
        
        // Procesar deep links de mascotas compartidas
        if (host === 'pet' || path.includes('/pet/')) {
          const slug = path.replace(/^\/+/, '');
          const petId = slug.split('/').pop();
          if (petId) {
            console.log('🐕 Shared pet deep link:', petId);
            navigate(`/pet/${petId}`, { replace: true });
            toast.info('Abriendo perfil de mascota compartido 🐕');
          }
          return;
        }
        
      } catch (error) {
        console.error('❌ Error parsing deep link:', error);
        isProcessing.current = false;
      }
    };

    const handleAppUrlOpen = (data: { url: string }) => {
      const now = Date.now();
      
      // Debounce: reducido a 1.5 segundos para mejor responsividad
      if (now - lastProcessedTime.current < 1500) {
        console.log('🔗 Ignoring duplicate event (within 1.5s)');
        return;
      }
      
      lastProcessedTime.current = now;
      isOAuthInProgress.current = true;
      processOAuthUrl(data.url);
    };

    let appUrlListener: any = null;
    let appStateListener: any = null;
    let browserFinishedListener: any = null;

    if (Capacitor.isNativePlatform()) {
      console.log('🔗 Registering deep link handlers');
      
      // Cold start
      App.getLaunchUrl().then((result) => {
        if (result?.url) {
          console.log('🚀 Cold start with URL:', result.url);
          processOAuthUrl(result.url);
        }
      }).catch(() => {
        console.log('📱 No launch URL');
      });
      
      // Warm start - deep link callback
      appUrlListener = App.addListener('appUrlOpen', handleAppUrlOpen);
      
      // Listener para cuando la app vuelve al foreground
      // Esto captura el caso donde el OAuth completó pero el deep link no llegó
      appStateListener = App.addListener('appStateChange', async (state: AppState) => {
        if (state.isActive && isOAuthInProgress.current) {
          console.log('📱 App returned to foreground during OAuth');
          // Pequeño delay para permitir que el auth state se sincronice
          await new Promise(resolve => setTimeout(resolve, 300));
          await checkSessionAndNavigate('appStateChange');
        }
      });
      
      // Listener para cuando el browser se cierra manualmente
      browserFinishedListener = Browser.addListener('browserFinished', async () => {
        console.log('🌐 Browser finished/closed by user');
        if (isOAuthInProgress.current) {
          // Esperar un momento y verificar si hay sesión
          await new Promise(resolve => setTimeout(resolve, 500));
          const navigated = await checkSessionAndNavigate('browserFinished');
          if (!navigated) {
            // El usuario cerró el browser sin completar OAuth
            console.log('ℹ️ OAuth cancelled by user');
            isOAuthInProgress.current = false;
          }
        }
      });
    }

    // Listener para reset en logout
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        console.log('🔓 User signed out - resetting OAuth state');
        hasProcessedAuth.current = false;
        isProcessing.current = false;
        isOAuthInProgress.current = false;
      }
    });

    return () => {
      if (appUrlListener) appUrlListener.remove();
      if (appStateListener) appStateListener.remove();
      if (browserFinishedListener) browserFinishedListener.remove();
      subscription.unsubscribe();
    };
  }, [navigate, checkSessionAndNavigate]);

  return null;
};